import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CardComponent } from '@vectronic/ui';
import { DateTimeHelper, MobileService } from '@vectronic/util';
import {
  DatatableFilterBooleanComponent,
  DatatableFilterDateComponent,
  DatatableFilterInputComponent,
  DatatableFilterIntegerComponent,
  DatatableFilterMinMaxComponent,
  DatatableFilterProcessIdComponent,
  DatatableFilterSelectionComponent,
  DatatableFilterSingleDateComponent,
  DatatableFilterSingleSelectComponent,
  DatatableFilterTimeSpanComponent,
} from '../../filter-components';
import {
  DatatableSelectionFilter,
  EditDatatableSelectionFilterPayload,
  SaveDatatableSelectionFilterPayload,
} from '../../types/datatable-configuration';
import { DatatableFilter } from '../../types/datatable-filter';
import { DatatableSavedFilterSelectComponent } from '../datatable-saved-filter-select/datatable-saved-filter-select.component';

@Component({
  selector: 'table-datatable-filters',
  templateUrl: './datatable-filters.component.html',
  styleUrls: ['./datatable-filters.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: DateTimeHelper.DATE_FORMAT,
        },
        display: {
          dateInput: DateTimeHelper.DATE_FORMAT,
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatInputModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    CardComponent,
    DatatableSavedFilterSelectComponent,
    DatatableFilterInputComponent,
    DatatableFilterSelectionComponent,
    DatatableFilterBooleanComponent,
    DatatableFilterDateComponent,
    DatatableFilterSingleSelectComponent,
    DatatableFilterSingleDateComponent,
    DatatableFilterIntegerComponent,
    DatatableFilterMinMaxComponent,
    DatatableFilterProcessIdComponent,
    DatatableFilterTimeSpanComponent,
  ],
})
export class DatatableFiltersComponent {
  private savedFiltersMenuTrigger = signal<MatMenuTrigger | undefined>(undefined);
  mobileService = inject(MobileService);

  filters = input.required<DatatableFilter<any>[]>();
  activeFilters = input.required<{ [key: string]: any }>();
  headless = input(false);
  position = input<'left' | 'right'>('left');
  width = input(240);
  hasApplyButton = input(false);
  selectionFilters = input<DatatableSelectionFilter[]>([]);
  selectedSelectionFilter = input<number>();
  showSavedFiltersButton = input(false);
  showOnDashboard = signal(false);

  filterChange = output<{ title: string; value: any }>();
  closeEvent = output<void>();
  selectionFilterChange = output<number | undefined>();
  saveSelectionFilter = output<SaveDatatableSelectionFilterPayload>();
  editSelectionFilter = output<EditDatatableSelectionFilterPayload>();
  deleteSelectionFilter = output<number>();

  expandedFilters = signal<{ [key: string]: boolean }>({});
  localFilters = signal<{ [key: string]: any }>({});
  savedFilterName = signal('');
  selectedSavedFilterValue = signal<number | undefined>(undefined);
  private pendingSelectedFilterValue = signal<number | undefined>(undefined);
  filterNameInput = viewChild<ElementRef<HTMLInputElement>>('filterNameInput');

  selectedFilter = computed(() => {
    const selectedValue = this.selectedSavedFilterValue();
    if (selectedValue === undefined) return undefined;
    return this.selectionFilters().find((filter) => filter.id === selectedValue);
  });

  isDefaultFilter = computed(() => {
    const selectedFilter = this.selectedFilter();
    return Boolean(selectedFilter?.defaultFilter || selectedFilter?.readonly);
  });

  constructor() {
    effect(() => this.localFilters.set(this.activeFilters()));
    effect(() => {
      const selectedSelectionFilter = this.selectedSelectionFilter();
      const selectedFilter = this.selectionFilters().find(
        (filter) => filter.id === selectedSelectionFilter,
      );

      untracked(() => {
        this.selectedSavedFilterValue.set(selectedFilter ? selectedSelectionFilter : undefined);
        this.savedFilterName.set(selectedFilter?.storageName ?? selectedFilter?.label ?? '');
        this.showOnDashboard.set(selectedFilter?.showOnDashboard ?? false);
      });
    });

    effect(() => {
      const active = this.activeFilters();
      const expandedFromActive = Object.keys(active).reduce(
        (acc, key) => {
          const value = active[key];
          const hasValue = value != undefined && (!Array.isArray(value) || value.length > 0);
          if (hasValue) acc[key] = true;
          return acc;
        },
        {} as { [key: string]: boolean },
      );
      untracked(() => this.expandedFilters.set(expandedFromActive));
    });

    effect(() => {
      const match = this.matchingSelectionFilter();
      const selectedSelectionFilter = this.selectedSelectionFilter();
      const pendingSelectedFilter = this.pendingSelectedFilterValue();
      const currentFilters = this.getSavableFilters();
      const pendingFilter = this.selectionFilters().find(
        (filter) => filter.id === pendingSelectedFilter,
      );
      untracked(() => {
        if (match) {
          this.pendingSelectedFilterValue.set(undefined);
          if (this.selectedSavedFilterValue() !== match.id) {
            this.selectedSavedFilterValue.set(match.id);
            this.savedFilterName.set(match.storageName ?? match.label);
            this.showOnDashboard.set(match.showOnDashboard ?? false);
          }
          // emit selection filter change(to save the state) if the
          // active filters match a different saved filter than the currently selected one
          if (this.selectedSelectionFilter() !== match.id) {
            this.selectionFilterChange.emit(match.id);
          }
        } else if (
          pendingSelectedFilter !== undefined &&
          pendingSelectedFilter === selectedSelectionFilter &&
          pendingFilter &&
          !this.areFilterValuesEqual(pendingFilter.filterValues ?? {}, currentFilters)
        ) {
          return;
        } else {
          this.pendingSelectedFilterValue.set(undefined);
          if (this.selectedSavedFilterValue() !== undefined) {
            this.selectedSavedFilterValue.set(undefined);
            this.savedFilterName.set('');
            this.showOnDashboard.set(false);
          }
        }
      });
    });
  }

  isExpanded(title: string) {
    const filter = this.filters()?.find((f) => f.title === title);
    if (filter?.hideToggle) return true;
    return this.expandedFilters()[title] || false;
  }

  isDuplicateSelectionFilterName(): boolean {
    return this.selectionFilters().some(
      (f) =>
        f.label.trim().toLowerCase() === this.savedFilterName().trim().toLowerCase() &&
        f.id !== this.selectedSavedFilterValue(),
    );
  }

  toggleFilter(title: string) {
    const filter = this.filters()?.find((f) => f.title === title);
    if (filter?.hideToggle) return;
    if (this.isFilterDisabled(title)) return;

    const current = this.expandedFilters();
    const isNowExpanded = !current[title];
    this.expandedFilters.set({ ...current, [title]: isNowExpanded });
    if (!isNowExpanded) this.emitChange(title, undefined);
  }

  emitChange(title: string, value: any) {
    if (this.isFilterDisabled(title)) return;
    if (this.getFilterValue(title) === value) return;
    if (this.hasApplyButton()) {
      const current = this.localFilters();
      this.localFilters.set({ ...current, [title]: value });
    } else {
      this.filterChange.emit({ title, value });
    }
  }

  onApply() {
    const local = this.localFilters();
    Object.keys(local).forEach((title) => this.filterChange.emit({ title, value: local[title] }));
  }

  onReset() {
    this.localFilters.set({});
    this.expandedFilters.set({});
    this.filters().forEach(({ title }) => this.filterChange.emit({ title, value: undefined }));
  }

  getFilterValue(title: string) {
    return this.hasApplyButton() ? this.localFilters()[title] : this.activeFilters()[title];
  }

  isFilterDisabled(title: string) {
    return Boolean(this.filters().find((filter) => filter.title === title)?.disabled);
  }

  private getSavableFilters() {
    const currentFilters = this.hasApplyButton() ? this.localFilters() : this.activeFilters();
    return Object.fromEntries(
      Object.entries(currentFilters).filter(
        ([, value]) => value != undefined && (!Array.isArray(value) || value.length > 0),
      ),
    );
  }

  private areFilterValuesEqual(left: { [key: string]: any }, right: { [key: string]: any }) {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key, index) => {
      if (key !== rightKeys[index]) return false;
      return JSON.stringify(left[key]) === JSON.stringify(right[key]);
    });
  }

  onSavedFilterNameInput(event: Event) {
    this.savedFilterName.set((event.target as HTMLInputElement).value);
  }

  onSavedFilterNameEnter() {
    if (this.canSubmitSelectionFilter() && !this.isDuplicateSelectionFilterName()) {
      this.onDoneSelectionFilter();
    } else {
      this.closeSavedFiltersPanel();
    }
  }

  canDeleteSelectionFilter() {
    const filter = this.selectedFilter();
    return Boolean(filter && !filter.defaultFilter);
  }

  canSubmitSelectionFilter() {
    const selectedFilter = this.selectedFilter();
    const name = this.savedFilterName().trim();

    if (selectedFilter && !selectedFilter.defaultFilter && selectedFilter.id !== undefined) {
      if (!name) return false;
      const isNameChanged = name !== (selectedFilter.storageName ?? selectedFilter.label);
      const isDashboardStateChanged =
        this.showOnDashboard() !== (selectedFilter.showOnDashboard ?? false);
      return isNameChanged || isDashboardStateChanged;
    }

    const filterValues = this.getSavableFilters();
    if (!Object.keys(filterValues).length || !name) {
      return false;
    }

    const matchingFilter = this.matchingSelectionFilter();
    return !matchingFilter || !matchingFilter.defaultFilter;
  }

  onDoneSelectionFilter() {
    if (!this.canSubmitSelectionFilter()) {
      this.closeSavedFiltersPanel();
      return;
    }

    const filterValues = this.getSavableFilters();
    const name = this.savedFilterName().trim();
    const selectedFilter = this.selectedFilter();

    if (selectedFilter && !selectedFilter.defaultFilter && selectedFilter.id !== undefined) {
      this.editSelectionFilter.emit({
        id: selectedFilter.id,
        name,
        filterValues,
        showOnDashboard: this.showOnDashboard(),
      });
    } else {
      this.saveSelectionFilter.emit({
        name,
        filterValues,
        showOnDashboard: this.showOnDashboard(),
      });
    }

    this.closeSavedFiltersPanel();
  }

  onShowDashboardToggle(checked: boolean) {
    this.showOnDashboard.set(checked);
  }

  onDeleteSelectionFilter() {
    const selectedFilter = this.selectedFilter();
    if (!selectedFilter || selectedFilter.defaultFilter || selectedFilter.id === undefined) return;
    this.deleteSelectionFilter.emit(selectedFilter.id);
    this.closeSavedFiltersPanel();
  }

  onSelectionFilterSelected(value: number) {
    this.pendingSelectedFilterValue.set(value);
    this.selectionFilterChange.emit(value);
  }

  matchingSelectionFilter() {
    const currentFilters = this.getSavableFilters();
    return this.selectionFilters().find((filter) =>
      this.areFilterValuesEqual(filter.filterValues ?? {}, currentFilters),
    );
  }

  setSavedFiltersMenuTrigger(trigger: MatMenuTrigger) {
    this.savedFiltersMenuTrigger.set(trigger);
    const selectedFilter = this.selectedFilter();
    this.savedFilterName.set(selectedFilter?.storageName ?? selectedFilter?.label ?? '');
    this.showOnDashboard.set(selectedFilter?.showOnDashboard ?? false);

    setTimeout(() => {
      this.filterNameInput()?.nativeElement?.focus();
    });
  }

  closeSavedFiltersPanel() {
    this.savedFiltersMenuTrigger()?.closeMenu?.();
  }
}
